// src/hooks/integrations/synapse/useSynapseFileSelection.ts
import { useState, useCallback } from 'react';

/**
 * Hook for managing file selection in Synapse components
 */
const useSynapseFileSelection = () => {
    const [selectedFiles, setSelectedFiles] = useState<string[]>([]);
    const [showFullPaths, setShowFullPaths] = useState<boolean>(true);

    /**
     * Toggle selection of a file
     * @param fileId ID of the file to toggle
     */
    const toggleFileSelection = useCallback((fileId: string) => {
        setSelectedFiles((prev) => {
            if (prev.includes(fileId)) {
                return prev.filter((id) => id !== fileId);
            } else {
                return [...prev, fileId];
            }
        });
    }, []);

    /**
     * Toggle selection of all files
     * @param allFileIds Array of all file IDs
     */
    const toggleSelectAll = useCallback(
        (allFileIds: string[]) => {
            if (selectedFiles.length === allFileIds.length) {
                setSelectedFiles([]);
            } else {
                setSelectedFiles([...allFileIds]);
            }
        },
        [selectedFiles],
    );

    /**
     * Clear all selected files
     */
    const clearSelection = useCallback(() => {
        setSelectedFiles([]);
    }, []);

    /**
     * Toggle showing full file paths
     */
    const toggleShowFullPaths = useCallback(() => {
        setShowFullPaths((prev) => !prev);
    }, []);

    return {
        selectedFiles,
        showFullPaths,
        toggleFileSelection,
        toggleSelectAll,
        clearSelection,
        toggleShowFullPaths,
    };
};

export default useSynapseFileSelection;
